import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  BeachAccess as BeachAccessIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import usePermisos from '../hooks/usePermisos';
import { apiRequest } from '../utils/api';
import { API_BASE_URL, getApiBaseUrl } from '../config';
import { generarHtmlRecibo, formatNumber, formatearFechaBD } from '../utils/reciboUtils';

const MiEquipo = () => {
  const navigate = useNavigate();
  const [empleados, setEmpleados] = useState([]);
  const [vacacionesPorEmpleado, setVacacionesPorEmpleado] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [recibosEmpleado, setRecibosEmpleado] = useState([]);
  const [modalRecibos, setModalRecibos] = useState(false);
  const { tienePermiso, esSuperAdmin, loading: permisosLoading, permisos, rol } = usePermisos();

  // Verificar si tiene permisos para ver el equipo
  const puedeVerEquipo = esSuperAdmin() || 
                        tienePermiso('usuarios', 'ver_equipo') || 
                        tienePermiso('legajos', 'ver_equipo') || 
                        tienePermiso('recibos', 'ver_equipo');

  useEffect(() => {
    if (!permisosLoading && puedeVerEquipo) {
      fetchEmpleados();
      fetchVacacionesEquipo();
    }
  }, [puedeVerEquipo, permisosLoading]);

  const fetchEmpleados = async () => {
    try {
      const data = await apiRequest('/api/referente/empleados');

      if (data && data.data) {
        setEmpleados(data.data);
      } else {
        setError('Error al cargar empleados');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  const fetchVacacionesEquipo = async () => {
    try {
      const data = await apiRequest('/api/vacaciones/equipo-resumen');

      if (Array.isArray(data)) {
        const porUsuario = {};
        data.forEach(v => {
          porUsuario[v.usuario_id] = v;
        });
        setVacacionesPorEmpleado(porUsuario);
      }
    } catch (error) {
      console.error('Error al cargar vacaciones del equipo:', error);
    }
  };

  const verRecibosEmpleado = async (empleado) => {
    try {
      setEmpleadoSeleccionado(empleado);
      const data = await apiRequest(`/api/referente/recibos?empleadoId=${empleado.id}`);

      if (data && data.data) {
        setRecibosEmpleado(data.data);
        setModalRecibos(true);
      } else {
        setError('Error al cargar recibos del empleado');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Error al cargar recibos del empleado');
    }
  };

  const imprimirRecibo = async (periodo) => {
    const registros = recibosEmpleado.filter(r => r.periodo === periodo);
    if (registros.length === 0) return;

    try {
      const printWindow = window.open('', '_blank');
      const reciboHtml = await generarHtmlRecibo(periodo, registros);
      printWindow.document.write(reciboHtml);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };
    } catch (error) {
      console.error('Error al imprimir recibo:', error);
      setError('Error al generar el recibo para impresión');
    }
  };

  const exportarExcel = () => {
    const datos = empleados.map((empleado) => {
      const vac = vacacionesPorEmpleado[empleado.id];
      let estadoVacaciones = 'Sin solicitudes';

      if (vac?.solicitud_pendiente) {
        estadoVacaciones = `Pendiente de aprobar (${formatDate(vac.solicitud_pendiente.fecha_inicio)} - ${formatDate(vac.solicitud_pendiente.fecha_fin)})`;
      } else if (vac?.proxima_aprobada) {
        estadoVacaciones = `Aprobadas (${formatDate(vac.proxima_aprobada.fecha_inicio)} - ${formatDate(vac.proxima_aprobada.fecha_fin)})`;
      }

      return {
        'Empleado': `${empleado.nombre} ${empleado.apellido}`,
        'DNI': empleado.dni,
        'Fecha Ingreso': formatDate(empleado.fecha_ingreso),
        'Estado': empleado.activo ? 'Activo' : 'Inactivo',
        'Días Disponibles': vac ? vac.dias_disponibles : '-',
        'Vacaciones': estadoVacaciones
      };
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(datos);

    worksheet['!cols'] = [
      { wch: 30 }, // Empleado
      { wch: 12 }, // DNI
      { wch: 14 }, // Fecha Ingreso
      { wch: 10 }, // Estado
      { wch: 16 }, // Días Disponibles
      { wch: 45 }  // Vacaciones
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Mi Equipo');

    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Mi_Equipo_${fecha}.xlsx`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatearFechaBD(dateString) || 'N/A';
  };

  if (loading || permisosLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando información del equipo...</Typography>
      </Box>
    );
  }

  // Verificar permisos antes de mostrar el contenido
  if (!puedeVerEquipo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          No tienes permisos para ver la información del equipo.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: '#1976d2',
          mb: 3
        }}
      >
        Mi Equipo de Trabajo
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Lista de empleados */}
      <Box sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            Empleados a mi cargo ({empleados.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={exportarExcel}
            disabled={empleados.length === 0}
          >
            Exportar Excel
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: '#1976d2',
                '& th': {
                  borderBottom: 'none',
                  color: 'white !important',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  py: 2
                }
              }}>
                <TableCell>👤 Empleado</TableCell>
                <TableCell>🆔 DNI</TableCell>
                <TableCell>📅 Fecha Ingreso</TableCell>
                <TableCell align="center">✅ Estado</TableCell>
                <TableCell align="center">🏖️ Días Disp.</TableCell>
                <TableCell align="center">🌴 Vacaciones</TableCell>
                <TableCell align="center">⚡ Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {empleados.map((empleado) => (
                <TableRow 
                  key={empleado.id}
                  sx={{ 
                    '&:nth-of-type(even)': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      transform: 'scale(1.001)',
                      transition: 'all 0.2s ease',
                    }
                  }}
                >
                  <TableCell sx={{ borderBottom: '1px solid #f0f0f0', padding: '12px' }}>
                    <Typography variant="body2" fontWeight="600" color="text.primary">
                      {empleado.nombre} {empleado.apellido}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #f0f0f0', padding: '12px' }}>
                    <Typography variant="body2">
                      {empleado.dni}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid #f0f0f0', padding: '12px' }}>
                    <Typography variant="body2">
                      {formatDate(empleado.fecha_ingreso)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: '1px solid #f0f0f0', padding: '12px' }}>
                    <Chip
                      label={empleado.activo ? 'Activo' : 'Inactivo'}
                      color={empleado.activo ? 'success' : 'error'}
                      size="small"
                      sx={{ 
                        borderRadius: 2,
                        fontWeight: 500,
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: '1px solid #f0f0f0', padding: '12px' }}>
                    {vacacionesPorEmpleado[empleado.id] ? (
                      <Chip
                        label={vacacionesPorEmpleado[empleado.id].dias_disponibles}
                        color={vacacionesPorEmpleado[empleado.id].dias_disponibles > 0 ? 'success' : 'default'}
                        variant="outlined"
                        size="small"
                        sx={{ borderRadius: 2 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: '1px solid #f0f0f0', padding: '12px' }}>
                    {(() => {
                      const vac = vacacionesPorEmpleado[empleado.id];
                      if (!vac) {
                        return <Typography variant="body2" color="text.secondary">-</Typography>;
                      }
                      if (vac.solicitud_pendiente) {
                        return (
                          <Tooltip title={`Solicitud del ${formatDate(vac.solicitud_pendiente.fecha_inicio)} al ${formatDate(vac.solicitud_pendiente.fecha_fin)} — click para ir a aprobar`}>
                            <Chip
                              icon={<BeachAccessIcon />}
                              label="Pendiente de aprobar"
                              color="warning"
                              size="small"
                              onClick={() => navigate('/vacaciones/referente')}
                              sx={{ borderRadius: 2, cursor: 'pointer' }}
                            />
                          </Tooltip>
                        );
                      }
                      if (vac.proxima_aprobada) {
                        return (
                          <Tooltip title="Vacaciones ya aprobadas">
                            <Chip
                              icon={<BeachAccessIcon />}
                              label={`${formatDate(vac.proxima_aprobada.fecha_inicio)} - ${formatDate(vac.proxima_aprobada.fecha_fin)}`}
                              color="success"
                              size="small"
                              sx={{ borderRadius: 2 }}
                            />
                          </Tooltip>
                        );
                      }
                      return (
                        <Typography variant="body2" color="text.secondary">Sin solicitudes</Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: '1px solid #f0f0f0', padding: '12px' }}>
                    <Tooltip title={empleado.total_recibos === 0 ? 'No hay recibos disponibles' : 'Ver recibos del empleado'}>
                      <span>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => verRecibosEmpleado(empleado)}
                          disabled={empleado.total_recibos === 0}
                          sx={{
                            bgcolor: empleado.total_recibos > 0 ? 'primary.50' : 'grey.200',
                            '&:hover': { 
                              bgcolor: empleado.total_recibos > 0 ? 'primary.100' : 'grey.200' 
                            }
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {empleados.length === 0 && (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6,
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            borderTop: '1px solid #e2e8f0'
          }}>
            <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No hay empleados a tu cargo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Los empleados aparecerán aquí cuando sean asignados a tu supervisión
            </Typography>
          </Box>
        )}
      </Box>

      {/* Modal de recibos del empleado */}
      <Dialog open={modalRecibos} onClose={() => setModalRecibos(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Recibos de {empleadoSeleccionado?.nombre} {empleadoSeleccionado?.apellido}
        </DialogTitle>
        <DialogContent>
          {recibosEmpleado.length > 0 ? (
            <Box>
              {Object.entries(
                recibosEmpleado.reduce((acc, recibo) => {
                  if (!acc[recibo.periodo]) acc[recibo.periodo] = [];
                  acc[recibo.periodo].push(recibo);
                  return acc;
                }, {})
              ).map(([periodo, recibos]) => (
                <Accordion key={periodo}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography variant="h6">
                        Período {periodo} 
                        <Chip 
                          label={recibos[0]?.tipo_liquidacion || 'N/A'} 
                          size="small" 
                          sx={{ ml: 2 }}
                        />
                        {recibos[0]?.fecha_firma && (
                          <Chip 
                            label="Firmado" 
                            color="success" 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Tooltip title="Imprimir recibo">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            imprimirRecibo(periodo);
                          }}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Código</TableCell>
                            <TableCell>Concepto</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell align="right">Importe</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recibos.map((recibo, index) => (
                            <TableRow key={index}>
                              <TableCell>{recibo.codigo}</TableCell>
                              <TableCell>{recibo.concepto}</TableCell>
                              <TableCell align="right">{recibo.cantidad}</TableCell>
                              <TableCell align="right">
                                <Typography 
                                  color={recibo.importe >= 0 ? 'success.main' : 'error.main'}
                                >
                                  ${formatNumber(recibo.importe)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
            <Typography>No hay recibos disponibles para este empleado.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalRecibos(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MiEquipo;